/* eslint-disable no-debugger */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './ClubTouchpointsDetailValidation';

import NavLinks from '../NavLinks';

import useBaseUsasClubData from '@usas/ui-web/state/clubBase/UseBaseUsasClubData';
import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

import useTouchpointTypeData from '../../../state/touchpointType/UseTouchpointTypeData';
import useClubTouchpointData from '../../../state/clubTouchpoints/UseClubTouchpointData';
import useTouchpointSubjectData from '../../../state/touchpointSubject/UseTouchpointSubjectData';
import useTouchpointContactTypeData from '../../../state/touchpointContactType/UseTouchpointContactTypeData';
import useClubStaffData from "../../../state/clubStaff/UseClubStaffData";

const useClubTouchpointsDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { touchpointTypeState } = useTouchpointTypeData();
  const { clubTouchpointState, postClubTouchpoint, putClubTouchpoint } = useClubTouchpointData();
  const { touchpointSubjectState } = useTouchpointSubjectData();
  const { touchpointContactTypeState } = useTouchpointContactTypeData();
  const { clubStaffState, getClubStaffMembers } = useClubStaffData();
  const [ clubStaffOptionsState, setClubStaffOptionsState ] = useState({ items: [], areItemsLoaded: false });

  const [state, setState] = useState({ tryRedirect: false });

  const {
    formState,
    errorState,
    updateFormState,
    handleSubmit,
    isDirty,
    setFormData,
    onValueTextPairChanged }
    = useForm(getFormInitialState, submitFormCallback, validate);

  useEffect(() => {
    if (clubTouchpointState.isObjLoaded === true) {
      navigate(NavLinks.CLUB_TOUCHPOINTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubTouchpointState]);

  useEffect(() => {
    if (baseUsasClubState.isObjLoaded === true) {
      if (baseUsasClubState.objData.orgUnitId) {
        const getClubStaffMembersPromise = getClubStaffMembers(baseUsasClubState.objData.orgUnitId, 1, true);

        if (getClubStaffMembersPromise !== null) {
          getClubStaffMembersPromise.then((newState) => {
            if (newState !== null) {
              const items = newState.arrayData.map(x => { return { id: x.memberId, name: `${x.firstName} ${x.lastName}` } });
              items.unshift({ id: '', name: '--' });

              setClubStaffOptionsState({
                ...clubStaffOptionsState,
                items: items,
                areItemsLoaded: true
              });
            }
          }).catch((e) => {
            //TODO context error
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUsasClubState.isObjLoaded]);

  useEffect(() => {
    if (
      location.state &&
      location.state.usasClubTouchpointId !== Constants.DEFAULT_ID &&
      touchpointTypeState.isLoaded === true &&
      touchpointSubjectState.isLoaded === true &&
      touchpointContactTypeState.isLoaded === true &&
      clubStaffOptionsState.areItemsLoaded === true
    ) {
      setFormData({
        ...FormData,
        usasClubTouchpointId: location.state.usasClubTouchpointId || Constants.DEFAULT_ID,
        touchpointNote: location.state.touchpointNote || "",
        touchpointDate: formatDate(location.state.touchpointDate) || Constants.BLANK_DATE_STRING,
        touchpointTypeId: location.state.touchpointTypeId || Constants.DEFAULT_ID,
        touchpointSubjectId: location.state.touchpointSubjectId || Constants.DEFAULT_ID,
        touchpointContactTypeId: location.state.touchpointContactTypeId || Constants.DEFAULT_ID,
        followUpDate: location.state.followUpDate ? formatDate(location.state.followUpDate) : Constants.BLANK_DATE_STRING,
        memberId: location.state.staffMemberId || '',
        hasCustomerSatisfactionSurvey: location.state.hasCustomerSatisfactionSurvey || false
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, touchpointTypeState, touchpointSubjectState, touchpointContactTypeState, clubStaffOptionsState]);

  function submitFormCallback() {
    const touchpointObj = {
      touchpointTypeId: formState.touchpointTypeId,
      touchpointSubjectId: formState.touchpointSubjectId,
      touchpointContactTypeId: formState.touchpointContactTypeId,
      touchpointNote: formState.touchpointNote,
      touchpointDate: formState.touchpointDate,
      followUpDate: formState.followUpDate === Constants.BLANK_DATE_STRING ? null : formState.followUpDate,
      staffMemberId: formState.memberId || '',
      hasCustomerSatisfactionSurvey: formState.hasCustomerSatisfactionSurvey || false
    };

    if (formState.usasClubTouchpointId !== Constants.DEFAULT_ID) {
      putClubTouchpoint(formState.usasClubTouchpointId, touchpointObj);
    } else {
      postClubTouchpoint(baseUsasClubState.objData.usasClubId, touchpointObj);
    }
  }

  const onSaveClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    handleSubmit();
    if (isDirty === false) {
      setState({ ...state, tryRedirect: true });
    }
  };

  const onCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(NavLinks.CLUB_TOUCHPOINTS);
  };

  function getFormInitialState() {
    return {
      usasClubTouchpointId: Constants.DEFAULT_ID,
      touchpointTypeId: Constants.DEFAULT_ID,
      touchpointTypeName: '',
      touchpointNote: '',
      touchpointDate: '',
      touchpointSubjectId: Constants.DEFAULT_ID,
      touchpointSubjectName: '',
      touchpointContactTypeId: Constants.DEFAULT_ID,
      touchpointContactTypeName: '',
      followUpDate: '',
      memberId: '',
      memberName: '',
      hasCustomerSatisfactionSurvey: false
    };
  };

  return {
    formState,
    errorState,
    touchpointTypeState,
    touchpointSubjectState,
    touchpointContactTypeState,
    clubTouchpointState,
    clubStaffState,
    clubStaffOptionsState,
    location,
    onFormValueChanged: updateFormState,
    handleSubmit,
    onSaveClicked,
    onCancelClicked,
    onValueTextPairChanged
  }
}

export default useClubTouchpointsDetail;